import React, {useContext, useEffect, useState} from "react";
import {StringParam, useQueryParam} from "use-query-params";
import {Asset} from "../models/Asset";
import {AssetCollection} from "../models/AssetCollection";
import {RootContext} from "../with-root";
import {FirebaseContext} from "./firebase";
import axios from "axios";

// TAssetContext represents the data type that this
// provider will inject in its decendents.
interface TAssetContext {
  asset: Asset | undefined;
  collection: AssetCollection | undefined;
  loading: boolean | undefined;
  collectionId: string | undefined;
  serialNumber: string | undefined;
  qrSuffix: string | undefined;
  isSwissPost: boolean;
  isGemeinschafts: boolean;
  isGemeinschafts3: boolean;
}

export const AssetContext = React.createContext<TAssetContext>({
  asset: undefined,
  collection: undefined,
  loading: true,
  collectionId: undefined,
  serialNumber: undefined,
  qrSuffix: undefined,
  isSwissPost: false,
  isGemeinschafts: false,
  isGemeinschafts3: false
});

const callGetAsset = async () => {
  console.debug("Getting asset...");
  try {

  } catch (e) {
    console.error(e);
  }
};

/*
 * The asset provider is responsible for querying the backend if
 * a collection ID and serial number are given. If the asset plus
 * its associated collection are already loaded it does nothing.
 * If either is not given, it does nothing.
 */
export const AssetProvider = ({children}) => {
  const [collectionId] = useQueryParam("collectionId", StringParam);
  const [serialNumber] = useQueryParam("serialNumber", StringParam);
  const [qrSuffixQueryParameter] = useQueryParam("qrSuffix", StringParam);
  const {setError} = useContext(RootContext);
  const {firebase} = useContext(FirebaseContext);
  const [data, setData] = useState<{
    asset: Asset;
    collection: AssetCollection;
    collectionId: string | undefined;
    serialNumber: string | undefined;
    qrSuffix: string | undefined;
  }>(null);

  // Check if the collection ID is Swiss Post, by comparing it against a fixed list.
  const swissPostCollectionIds = [
    "c11", "c12", "c13", "c14", "c15", "c16",
    "c21", "c22", "c23", "c24", "c25", "c26"
  ];
  const gemeinschaft1CollectionIds = [
    "c11", "c12", "c13", "c14", "c15", "c16",
    "l11", "l12", "l13", "l14", "l15", "l16"
  ];
  const gemeinschaft2CollectionIds = [
    "c21", "c22", "c23", "c24", "c25", "c26",
    "l21", "l22", "l23", "l24", "l25", "l26"
  ];
  const gemeinschaft3CollectionIds = [
    "l31", "l32", "l33", "l34", "l35", "l36"
  ];
  const isGemeinschafts3 = collectionId && gemeinschaft3CollectionIds.includes(collectionId);
  const isGemeinschafts = collectionId
      && (
          isGemeinschafts3
          || gemeinschaft2CollectionIds.includes(collectionId)
          || gemeinschaft1CollectionIds.includes(collectionId));
  const isSwissPost = collectionId && (isGemeinschafts3 || swissPostCollectionIds.includes(collectionId));

  // If the data was already loaded default to `loading` == false
  const [loading, setLoading] = useState(
    Boolean(!data && collectionId && serialNumber)
  );

  useEffect(() => {
    // Expect it to be the last query param without a value
    const searchParams = new URLSearchParams(location.search);
    let qrSuffix: string | undefined = qrSuffixQueryParameter;
    if (!qrSuffix) {
      for (var searchParam of searchParams.entries()) {
        if (searchParam.length === 2 && searchParam[1].length === 0) {
          qrSuffix = searchParam[0];
          console.debug(`Detected qrSuffix: ${qrSuffix}`);
          break;
        }
      }
    }
    const fetchAsset = async () => {
      try {
        const firestore = firebase.firestore();

        setLoading(data === null);
        setError(null);

        const res = await axios.post(process.env.GATSBY_FIREBASE_FUNCTION_GET_ASSET, {
          "collectionId": collectionId,
          "serialNumber": serialNumber
        });

        // Handle the case where the asset is not found
        if (res.status !== 200) {
          throw new Error(`Asset not found for collection ID ${collectionId} and serial number ${serialNumber}`);
        }

        const assetCollection = res.data.assetCollection as AssetCollection;
        const asset = res.data.asset as Asset;

        console.log({asset, assetCollection, qrSuffix});
        setData({
          asset: asset,
          collection: assetCollection,
          collectionId: collectionId,
          serialNumber: serialNumber,
          qrSuffix: qrSuffix
        });
      } catch (error) {
        console.error(error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    // Only query the backend if the asset was not already loaded.
    // This is necessary to not fetch the asset again when navigating
    // back and forth to the assets page.
    if (!data && collectionId && serialNumber && firebase) {
      fetchAsset();
    }
  }, [collectionId, serialNumber, firebase]);

  return (
    <AssetContext.Provider
      value={{
        ...data,
        loading: loading,
        isSwissPost: isSwissPost,
        isGemeinschafts: isGemeinschafts,
        isGemeinschafts3: isGemeinschafts3
      }}
    >
      {children}
    </AssetContext.Provider>
  );
};
